/* eslint-disable consistent-return */
import { apiPaths, getApiPath, httpStatus } from '../../constants'
import { Api } from '../../middlewares'
import { Logger, LoggerTypes } from '../../utils'

// -- GET (array of objects)
// export const GET_FLOW_CONDITION_CHECKS_SUCCESS = 'GET_FLOW_CONDITION_CHECKS_SUCCESS'
// export const GET_FLOW_CONDITION_CHECKS_FAILURE = 'GET_FLOW_CONDITION_CHECKS_FAILURE'

// -- CREATE
export const CREATE_FLOW_CONDITION_CHECK_SUCCESS = 'CREATE_FLOW_CONDITION_CHECK_SUCCESS'
export const CREATE_FLOW_CONDITION_CHECK_FAILURE = 'CREATE_FLOW_CONDITION_FAILURE'

// -- UPDATE
export const UPDATE_FLOW_CONDITION_CHECK_SUCCESS = 'UPDATE_FLOW_CONDITION_CHECK_SUCCESS'
export const UPDATE_FLOW_CONDITION_CHECK_FAILURE = 'UPDATE_FLOW_CONDITION_CHECK_FAILURE'

// -- DELETE
export const DELETE_FLOW_CONDITION_CHECK_SUCCESS = 'DELETE_FLOW_CONDITION_CHECK_SUCCESS'
export const DELETE_FLOW_CONDITION_CHECK_FAILURE = 'DELETE_FLOW_CONDITION_CHECK_FAILURE'

export const CACHE_CREATE_FLOW_CONDITION_CHECK = 'CACHE_CREATE_FLOW_CONDITION_CHECK'
export const CACHE_DELETE_FLOW_CONDITION_CHECK = 'CACHE_DELETE_FLOW_CONDITION_CHECK'

// ____________________________________________________________

/**
 * Get the flow condition check form the API and broadcast two types of events:
 * - GET_FLOW_CONDITION_CHECKS_SUCCESS: in case of success.
 * - GET_FLOW_CONDITION_CHECKS_FAILURE: in case of failure.
 *
 * @param {uuid} pid Project Id UUID.
 * @param {uuid} sid Scenario Id UUID.
 * @param {uuid} fid FlowGroup Id UUID.
 * @param {uuid} cid Flow condition Id UUID.
 * @returns {Promise<object>}
 */
// export const getFlowConditionChecks = (pid, sid, fid, cid) => async (dispatch) => {
//   const path = getApiPath(apiPaths.FLOW_CONDITION_CHECK_LIST, { pid, sid, fid, cid })
//   const api = new Api(path)
//   const result = await api.get()

//   Logger.trace(LoggerTypes.ACTION, '[ACTION] flowConditionsActions.getFlowConditionChecks()', { path, result })

//   if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
//     return dispatch({
//       type: GET_FLOW_CONDITION_CHECKS_SUCCESS,
//       data: result.data.items || []
//     })
//   }
//   return dispatch({
//     type: GET_FLOW_CONDITION_CHECKS_FAILURE,
//     data: result.data
//   })
// }

/**
 * Add the definition of a condition check in the flowGroup's cache.
 *
 * @param {object} flowGroup the flow group of the task
 * @param {object} cid the identifier of the condition to be updated
 * @param {object} check the check to be added
 * @returns {object}
 */
export const cacheCreateFlowConditionCheck = (flowGroup, cid, check) => ({
  type: CACHE_CREATE_FLOW_CONDITION_CHECK,
  data: {
    flowGroup,
    cid,
    check
  }
})

/**
 * Remove the definition of a condition check from the flowGroup's cache.
 *
 * @param {object} flowGroup the flow group of the task
 * @param {object} cid the identifier of the condition to be updated
 * @param {object} checkId the identifier of the check to be removed
 * @returns {object}
 */
export const cacheDeleteFlowConditionCheck = (flowGroup, cid, checkId) => ({
  type: CACHE_DELETE_FLOW_CONDITION_CHECK,
  data: {
    flowGroup,
    cid,
    checkId
  }
})

/**
 * Create a flowCondition check broadcast two types of events:
 * - CREATE_FLOW_CONDITION_CHECK_SUCCESS: in case of success.
 * - CREATE_FLOW_CONDITION_CHECK_FAILURE: in case of failure.
 *
 * @param {uuid} pid Project Id UUID
 * @param {uuid} sid Scenario Id UUID
 * @param {uuid} fid FlowGroup Id UUID
 * @param {uuid} cid Flow condition Id UUID
 * @param {object} flowCheck Flow condition check to create
 * @returns {Promise<object>}
 */
export const createFlowConditionCheck = (pid, sid, fid, cid, flowCheck) => async (dispatch) => {
  if (!flowCheck) {
    return
  }

  const path = getApiPath(apiPaths.FLOW_CONDITION_CHECK_CREATE, { pid, sid, fid, cid })
  const api = new Api(path)
  const result = await api.create(flowCheck)

  Logger.trace(LoggerTypes.ACTION, '[ACTION] flowConditionsActions.createFlowConditionCheck()', {
    path,
    result,
    flowCheck
  })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: CREATE_FLOW_CONDITION_CHECK_SUCCESS,
      data: result.data
    })
  }
  return dispatch({
    type: CREATE_FLOW_CONDITION_CHECK_FAILURE,
    data: result.data
  })
}

/**
 * Update the values of a flowCondition check; broadcast two types of events:
 * - UPDATE_FLOW_CONDITION_CHECK_SUCCESS: in case of success.
 * - UPDATE_FLOW_CONDITION_CHECK_FAILURE: in case of failure.
 *
 * @param {uuid} pid Project Id UUID
 * @param {uuid} sid Scenario Id UUID
 * @param {uuid} fid FlowGroup Id UUID
 * @param {uuid} cid Flow condition Id UUID
 * @param {object} conditionCheck Flow condition to update (must contain `id` UUID attribute)
 * @returns {Promise<object>}
 */
export const updateFlowConditionCheck = (pid, sid, fid, cid, conditionCheck) => async (dispatch) => {
  if (!conditionCheck) {
    return
  }

  const path = getApiPath(apiPaths.FLOW_CONDITION_CHECK_UPDATE, { pid, sid, fid, cid, kid: conditionCheck.id })
  const api = new Api(path)
  const result = await api.update(conditionCheck)

  Logger.trace(LoggerTypes.ACTION, '[ACTION] flowConditionsActions.updateFlowConditionCheck()', {
    path,
    result,
    conditionCheck
  })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: UPDATE_FLOW_CONDITION_CHECK_SUCCESS,
      data: result.data
    })
  }
  return dispatch({
    type: UPDATE_FLOW_CONDITION_CHECK_FAILURE,
    data: result.data
  })
}

/**
 * Delete a flowCondition check, broadcast two types of events:
 * - DELETE_FLOW_CONDITION_CHECK_SUCCESS: in case of success.
 * - DELETE_FLOW_CONDITION_CHECK_FAILURE: in case of failure.
 *
 * @param {uuid} pid Project Id UUID.
 * @param {uuid} sid Scenario Id UUID.
 * @param {uuid} fid FlowGroup Id UUID.
 * @param {uuid} cid Condition Id UUID.
 * @param {uuid} kid check Id UUID.
 * @returns {Promise<object>}
 */
export const deleteFlowConditionCheck = (pid, sid, fid, cid, kid) => async (dispatch) => {
  const path = getApiPath(apiPaths.FLOW_CONDITION_CHECK_DELETE, { pid, sid, fid, cid, kid })
  const api = new Api(path)
  const result = await api.delete()

  Logger.trace(LoggerTypes.ACTION, '[ACTION] flowConditionsActions.deleteFlowConditionCheck()', {
    path,
    result
  })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: DELETE_FLOW_CONDITION_CHECK_SUCCESS,
      data: result.data
    })
  }
  return dispatch({
    type: DELETE_FLOW_CONDITION_CHECK_FAILURE,
    data: result.data
  })
}
